import React, { useState, useCallback, useEffect } from 'react';
import { voteService } from '../../services/vote';
import { useAuth } from '../../context/AuthContext';
import { useMasterEvent } from '../../context/MasterEventContext';
import { authService } from '../../services/auth';
import Avatar from '../shared/Avatar';

export default function VoteActivity({ activity }) {
  const { user } = useAuth();
  const { isAdmin } = useMasterEvent();
  const allUsers = authService.getAllUsers();
  const [poll, setPoll] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);

  const loadPoll = useCallback(() => {
    const p = voteService.getForActivity(activity.id);
    if (p) setPoll(voteService.getResults(p.id));
    else setPoll(null);
  }, [activity.id]);

  useEffect(() => { loadPoll(); }, [loadPoll]);

  const handleCreate = () => {
    if (!question.trim() || options.filter((o) => o.trim()).length < 2) return;
    voteService.create({
      activityId: activity.id,
      masterEventId: activity.masterEventId,
      question: question.trim(),
      options: options.filter((o) => o.trim()),
      createdBy: user.id,
    });
    setQuestion(''); setOptions(['', '']); setShowCreate(false);
    loadPoll();
  };

  const handleVote = (optionId) => {
    if (!poll || !user) return;
    voteService.castVote(poll.id, optionId, user.id);
    loadPoll();
  };

  const handleClose = () => { voteService.closePoll(poll.id); loadPoll(); };
  const handleReopen = () => { voteService.reopenPoll(poll.id); loadPoll(); };
  const handleDelete = () => {
    if (!window.confirm('Delete this poll?')) return;
    voteService.deletePoll(poll.id); loadPoll();
  };

  const isCreator = poll?.createdBy === user?.id;
  const canManage = isCreator || isAdmin;

  // No poll yet - show create
  if (!poll) {
    return (
      <div className="actd-inline">
        <div className="actd-inline-header">
          <span className="actd-inline-icon">🗳️</span>
          <div>
            <h3 className="actd-inline-name">{activity.name}</h3>
            {activity.description && <p className="actd-inline-desc">{activity.description}</p>}
          </div>
        </div>

        {showCreate ? (
          <div style={{ marginTop: 12 }}>
            <input type="text" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="What are we voting on?"
              style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, outline: 'none', marginBottom: 10, boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              {options.map((opt, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="text" value={opt} onChange={(e) => { const n = [...options]; n[i] = e.target.value; setOptions(n); }}
                    placeholder={`Option ${i + 1}`}
                    style={{ flex: 1, padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#fff', fontSize: 13, outline: 'none' }} />
                  {options.length > 2 && (
                    <button onClick={() => setOptions(options.filter((_, j) => j !== i))}
                      style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 14 }}>✕</button>
                  )}
                </div>
              ))}
              <button onClick={() => setOptions([...options, ''])}
                style={{ padding: '6px 12px', background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 6, color: 'rgba(255,255,255,0.5)', fontSize: 12, cursor: 'pointer' }}>
                + Add Option
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleCreate} disabled={!question.trim() || options.filter((o) => o.trim()).length < 2}
                style={{ padding: '8px 18px', background: '#22c55e', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: question.trim() && options.filter((o) => o.trim()).length >= 2 ? 1 : 0.5 }}>
                Create Poll
              </button>
              <button onClick={() => setShowCreate(false)}
                style={{ padding: '8px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14, marginBottom: 10 }}>No poll created yet.</p>
            <button onClick={() => setShowCreate(true)}
              style={{ padding: '8px 18px', background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.25)', borderRadius: 8, color: '#c084fc', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              🗳️ Create Poll
            </button>
          </div>
        )}
      </div>
    );
  }

  // Poll exists - show voting UI
  const totalVotes = poll.totalVotes || 0;

  return (
    <div className="actd-inline">
      <div className="actd-inline-header">
        <span className="actd-inline-icon">🗳️</span>
        <div style={{ flex: 1 }}>
          <h3 className="actd-inline-name">{activity.name}</h3>
          {activity.description && <p className="actd-inline-desc">{activity.description}</p>}
        </div>
        <span className={`actd-status actd-status--${poll.status === 'open' ? 'active' : 'concluded'}`}>
          {poll.status}
        </span>
      </div>

      <div style={{ margin: '16px 0' }}>
        <h4 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 14px' }}>{poll.question}</h4>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {poll.options.map((opt) => {
            const hasVoted = opt.votes.includes(user?.id);
            const isOpen = poll.status === 'open';
            return (
              <button key={opt.id}
                onClick={() => isOpen && handleVote(opt.id)}
                disabled={!isOpen}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                  background: hasVoted ? 'rgba(168,85,247,0.12)' : 'rgba(255,255,255,0.04)',
                  border: hasVoted ? '1px solid rgba(168,85,247,0.3)' : '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 10, cursor: isOpen ? 'pointer' : 'default',
                  transition: 'all 0.15s', textAlign: 'left', color: '#fff', position: 'relative', overflow: 'hidden',
                }}>
                {/* Progress bar */}
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0, width: `${opt.percentage}%`,
                  background: hasVoted ? 'rgba(168,85,247,0.08)' : 'rgba(255,255,255,0.03)',
                  transition: 'width 0.3s ease', borderRadius: 10,
                }} />
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                  <span style={{
                    width: 20, height: 20, borderRadius: '50%', border: hasVoted ? '2px solid #a855f7' : '2px solid rgba(255,255,255,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 10,
                    background: hasVoted ? '#a855f7' : 'transparent', color: hasVoted ? '#fff' : 'transparent',
                  }}>✓</span>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{opt.text}</span>
                </div>
                <div style={{ position: 'relative', textAlign: 'right', flexShrink: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: hasVoted ? '#a855f7' : 'rgba(255,255,255,0.6)' }}>{opt.percentage}%</span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginLeft: 4 }}>({opt.count})</span>
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ marginTop: 10, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
          {totalVotes} vote{totalVotes !== 1 ? 's' : ''} total
          {!poll.anonymous && totalVotes > 0 && (
            <span style={{ marginLeft: 8 }}>
              — {poll.options.flatMap((o) => o.votes).map((uid) => <Avatar key={uid} user={allUsers.find((u) => u.id === uid)} size={14} style={{display:'inline-block'}} />)}
            </span>
          )}
        </div>
      </div>

      {/* Admin controls */}
      {canManage && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          {poll.status === 'open' ? (
            <button onClick={handleClose} style={{ padding: '6px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, color: '#f87171', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Close Poll
            </button>
          ) : (
            <button onClick={handleReopen} style={{ padding: '6px 14px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 6, color: '#22c55e', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Reopen
            </button>
          )}
          <button onClick={handleDelete} style={{ padding: '6px 14px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)', borderRadius: 6, color: '#f87171', fontSize: 12, cursor: 'pointer' }}>
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
